import { useMutation, useQueryClient } from '@tanstack/react-query'
import { postRequest, putRequest } from '../api'
import { displayErrorToast } from '../helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { reactivateGuild } from '../api/guild'
import {
  updateAutoSetupSettings,
  updateEncounterOrder,
  updatePlanAbilityDefaults,
  updateTeam,
} from '../api/team'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import {
  claimExistingManualCharacter,
  claimManualCharacter,
  ManualCharacterInput,
  ManualCharacterUpdate,
  removeCharactersFromGuildTeams,
  unclaimManualCharacter,
  updateManualCharacter,
} from '../api/user'
interface UpdateEntityOptions<T> {
  entityType: string
  id?: number
  queryKey?: string
  queryKeyValue?: string | number
  parentEntityType?: string
  parentId?: number
  stringifyValues?: (keyof T)[]
  onSuccess?: (data: RecursivePartial<T>) => void
  showSuccessToast?: boolean
}

export const useUpdateTeam = <T extends wowaudit.Team>(
  team: wowaudit.Team,
  onSuccess?: (data: RecursivePartial<T>) => void,
  onError?: (error: Error) => void,
  { showSuccessToast = true }: { showSuccessToast?: boolean } = {},
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RecursivePartial<T>) => {
      return putRequest(`/api/teams/${team.id}`, { team: data })
    },
    onSuccess: (data: RecursivePartial<T>) => {
      queryClient.setQueryData(['team', { path: team.path }], data)
      if (showSuccessToast) {
        toast.success(t('shared.changes-saved'))
      }
      onSuccess?.(data)
    },
    onError: (error) => {
      displayErrorToast(error, t)
      onError?.(error)
    },
  })
}

export const useUpdatePlanAbilityDefaults = (team: wowaudit.Team) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planAbilityDefaults: wowaudit.Team['planAbilityDefaults']) =>
      updatePlanAbilityDefaults(team.id, planAbilityDefaults),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['team', { path: team.path }],
        (current: wowaudit.Team | undefined) =>
          current
            ? { ...current, planAbilityDefaults: data.planAbilityDefaults }
            : current,
      )
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useUpdateAutoSetupSettings = (team: wowaudit.Team) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (autoSetupSettings: wowaudit.Team['autoSetupSettings']) =>
      updateAutoSetupSettings(team.id, autoSetupSettings),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['team', { path: team.path }],
        (current: wowaudit.Team | undefined) =>
          current
            ? { ...current, autoSetupSettings: data.autoSetupSettings }
            : current,
      )
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useUpdateEncounterOrder = (team: wowaudit.Team) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const teamQuery = { queryKey: ['team'] as const }

  const patchEncounterOrders = (
    encounterOrders: wowaudit.Team['encounterOrders'],
  ) => {
    queryClient.setQueriesData<wowaudit.Team>(teamQuery, (current) =>
      current && current.id === team.id
        ? { ...current, encounterOrders }
        : current,
    )
  }

  return useMutation({
    mutationFn: (payload: {
      seasonId: number
      difficulty: Difficulty
      encounterIds: number[]
    }) => updateEncounterOrder(team.id, payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries(teamQuery)
      const previous = queryClient.getQueriesData<wowaudit.Team>(teamQuery)
      const seasonKey = payload.seasonId.toString()
      queryClient.setQueriesData<wowaudit.Team>(teamQuery, (current) => {
        if (!current || current.id !== team.id) return current
        return {
          ...current,
          encounterOrders: {
            ...current.encounterOrders,
            [seasonKey]: {
              ...(current.encounterOrders?.[seasonKey] ?? {}),
              [payload.difficulty]: payload.encounterIds,
            },
          },
        }
      })
      return { previous }
    },
    onError: (error, _variables, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
      displayErrorToast(error, t)
    },
    onSuccess: (data) => {
      patchEncounterOrders(data.encounterOrders)
    },
  })
}

export const useUpdateOnboardingStep = (teamId: number) => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (onboardingStep: string | null) =>
      updateTeam(teamId, { onboardingStep }),
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useReactivateGuild = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (guildReference: wowaudit.GuildReference) =>
      reactivateGuild(guildReference.id),
    onSuccess: (data: wowaudit.Guild, guildReference) => {
      queryClient.setQueryData(['guild', { path: guildReference.path }], data)
      queryClient.setQueryData<wowaudit.PageInfo>(['pageInfo'], (pageInfo) =>
        pageInfo?.user
          ? {
              ...pageInfo,
              user: {
                ...pageInfo.user,
                teamReferences: pageInfo.user.teamReferences.map((team) =>
                  team.ownerReference.id === data.id
                    ? {
                        ...team,
                        ownerReference: {
                          ...team.ownerReference,
                          active: data.active,
                        },
                      }
                    : team,
                ),
              },
            }
          : pageInfo,
      )
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useRemoveCharactersFromGuild = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (guildReference: wowaudit.GuildReference) =>
      removeCharactersFromGuildTeams(guildReference.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pageInfo'] })
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useUpdateEntity = <T>({
  entityType,
  id,
  queryKey = 'id',
  queryKeyValue = id,
  parentEntityType,
  parentId,
  stringifyValues = [],
  onSuccess,
  showSuccessToast = true,
}: UpdateEntityOptions<T>) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const baseUrl = parentEntityType
    ? `/api/${parentEntityType}s/${parentId}/${entityType}s`
    : `/api/${entityType}s`

  return useMutation({
    mutationFn: (data: RecursivePartial<T>) => {
      const transformedData: Record<string, unknown> = {}
      Object.entries(data).forEach(([key, value]) => {
        if (stringifyValues.includes(key as keyof T)) {
          transformedData[key] = JSON.stringify(value)
        } else {
          transformedData[key] = value
        }
      })

      return id
        ? putRequest(`${baseUrl}/${id}`, { [entityType]: transformedData })
        : postRequest(`${baseUrl}`, { [entityType]: transformedData })
    },
    onSuccess: (data: RecursivePartial<T>) => {
      queryClient.setQueryData(
        [entityType, { [queryKey]: queryKeyValue }],
        data,
      )
      if (showSuccessToast) {
        toast.success(t('shared.changes-saved'))
      }
      onSuccess?.(data)
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

const useInvalidatePageInfoOnSuccess = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pageInfo'] }),
    onError: (error: Error) => displayErrorToast(error, t),
  }
}

export const useClaimManualCharacter = () =>
  useMutation({
    mutationFn: (input: ManualCharacterInput) => claimManualCharacter(input),
    ...useInvalidatePageInfoOnSuccess(),
  })

// Manual characters also show up in team rosters and guild candidates.
const useInvalidateManualCharacterQueries = () => {
  const queryClient = useQueryClient()

  return () => {
    for (const queryKey of [
      ['pageInfo'],
      ['team'],
      ['teamUsers'],
      ['guildCandidates'],
    ]) {
      queryClient.invalidateQueries({ queryKey })
    }
  }
}

export const useUpdateManualCharacter = () => {
  const { t } = useTranslation()
  const invalidate = useInvalidateManualCharacterQueries()

  return useMutation({
    mutationFn: ({
      characterId,
      ...attributes
    }: ManualCharacterUpdate & { characterId: number }) =>
      updateManualCharacter(characterId, attributes),
    onSuccess: invalidate,
    onError: (error: Error) => displayErrorToast(error, t),
  })
}

export const useClaimExistingManualCharacter = () => {
  const { t } = useTranslation()
  const invalidate = useInvalidateManualCharacterQueries()

  return useMutation({
    mutationFn: (character: wowaudit.CharacterReference) =>
      claimExistingManualCharacter(character.id),
    onSuccess: (_data, character) => {
      toast.success(`Claimed ${character.name}`)
      invalidate()
    },
    onError: (error: Error) => displayErrorToast(error, t),
  })
}

export const useUnclaimManualCharacter = () => {
  const { t } = useTranslation()
  const invalidate = useInvalidateManualCharacterQueries()

  return useMutation({
    mutationFn: (characterId: number) => unclaimManualCharacter(characterId),
    onSuccess: invalidate,
    onError: (error: Error) => displayErrorToast(error, t),
  })
}
